import { Theme } from "@mui/material";
import { shadows } from "configs";
import {
  base,
  blue,
  blueDark,
  blueLight,
  cyan,
  error,
  fuchsia,
  gray,
  grayBlue,
  grayCool,
  grayIron,
  grayModern,
  grayNeutral,
  grayTrue,
  grayWarm,
  green,
  greenLight,
  indigo,
  moss,
  orange,
  orangeDark,
  pink,
  primary,
  purple,
  rose,
  success,
  teal,
  violet,
  warning,
  yellow,
} from "configs/colors";
import { HTMLFontSize } from "configs/variables";

interface PaletteColorOverride {
  light: string;
  main: string;
  dark: string;
  contrastText: string;
  25: string;
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

/**
 * Retrieves the primary palette color from the given theme.
 *
 * @param theme - The theme object containing palette information.
 * @returns The primary palette color as a `PaletteColorOverride`.
 */
export const getPrimaryPaletteColor = (theme: Theme) => {
  return theme.palette.primary as PaletteColorOverride;
};

export const generateFormFieldStyles = ({
  theme,
  color,
}: {
  theme: Theme;
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "grey";
}) => {
  const palette = theme.palette[color ?? "primary"] as unknown as Record<
    number | string,
    string
  >;
  const errorPalette = theme.palette.error as unknown as Record<
    number | string,
    string
  >;
  return {
    normal: {
      backgroundColor: base.White,
      borderColor: gray[300],
      boxShadow: shadows.shadows.xs,
      hover: {
        borderColor: palette[300],
      },
      focus: {
        borderColor: palette[300],
        boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${palette[100]}`,
      },
    },
    disabled: {
      backgroundColor: gray[100],
      borderColor: gray[300],
      color: gray[500],
    },
    error: {
      hover: {
        borderColor: errorPalette[300],
      },
      focus: {
        borderColor: errorPalette[300],
        boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${errorPalette[100]}`,
      },
    },
  };
};

/**
 * Convert from "pixel" to "rem"
 */
export const rem = (fs: number) => `${fs / HTMLFontSize}rem`;

/**
 * Convert from "pixel" to "lineHeight"
 */
export const lineHeight = (
  fs: number,
  basedOnRem: boolean | undefined = true
) => `${fs / HTMLFontSize}${basedOnRem ? "rem" : ""}`;

export const randomColor = (
  variant?: 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950
) => {
  const colors = [
    blue,
    blueDark,
    blueLight,
    cyan,
    error,
    fuchsia,
    gray,
    grayBlue,
    grayCool,
    grayIron,
    grayModern,
    grayNeutral,
    grayTrue,
    grayWarm,
    green,
    greenLight,
    indigo,
    moss,
    orange,
    orangeDark,
    pink,
    primary,
    purple,
    rose,
    success,
    teal,
    violet,
    warning,
    yellow,
  ];
  const variants = [25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
  const colorIndex = Math.floor(Math.random() * colors.length);
  const color = colors[colorIndex];
  const variantIndex = Math.floor(Math.random() * variants.length);
  const colorVariant = variant ?? variants[variantIndex];
  return (color as Record<string, string>)[colorVariant];
};
